import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Trash2, Edit, Loader2, PlayCircle, Inbox,
  Settings2, Circle, Hand, RefreshCw,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PRIORITY_LABELS = { 1: 'Critical', 2: 'High', 3: 'Moderate', 4: 'Low' };

export default function CsmAwaAdminPage() {
  const [rules, setRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const [queues, setQueues] = useState([]);
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // rule being edited
  const [showNew, setShowNew] = useState(false);
  const [tab, setTab] = useState('rules');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [r, a, g, q, p] = await Promise.all([
        axios.get(`${API_URL}/api/csm/assignment-rules`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/csm/agents`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/csm/assignment-groups`, { headers: authHeader() }).catch(() => ({ data: { groups: [] } })),
        axios.get(`${API_URL}/api/csm/queues`, { headers: authHeader() }).catch(() => ({ data: { queues: [] } })),
        axios.get(`${API_URL}/api/csm/work-pool`, { headers: authHeader() }).catch(() => ({ data: { tickets: [] } })),
      ]);
      setRules(r.data?.rules || []);
      setAgents(a.data?.agents || []);
      setGroups(g.data?.groups || []);
      setQueues(q.data?.queues || []);
      setPool(p.data?.tickets || []);
    } catch (err) {
      toast.error('Failed to load AWA config');
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await axios.delete(`${API_URL}/api/csm/assignment-rules/${id}`, { headers: authHeader() });
      toast.success('Rule deleted');
      loadAll();
    } catch (err) { toast.error('Delete failed'); }
  };

  const runAwa = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/csm/awa/run`, {}, { headers: authHeader() });
      toast.success(`Sweep: ${r.data.assigned} assigned, ${r.data.pooled} pooled, ${r.data.no_match} unmatched`);
      loadAll();
    } catch (err) { toast.error('Sweep failed'); }
  };

  const claim = async (ticketId) => {
    try {
      await axios.post(`${API_URL}/api/csm/tickets/${ticketId}/claim`, {}, { headers: authHeader() });
      toast.success('Ticket claimed');
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Claim failed'); }
  };

  const updateCapacity = async (agent) => {
    const cur = agent.max_concurrent_tickets;
    const v = window.prompt(`Capacity for ${agent.name} (current ${cur}):`, String(cur));
    if (!v) return;
    const n = parseInt(v, 10);
    if (Number.isNaN(n) || n < 1) { toast.error('Enter a positive number'); return; }
    try {
      // Each agent can only update their own via /me — admins enroll others; for now we self-edit.
      if (agent.user_id === window.localStorage.getItem('savenes_user_id')) {
        await axios.put(`${API_URL}/api/csm/agents/me/capacity`, { max_concurrent_tickets: n }, { headers: authHeader() });
      } else {
        toast.message('Only the agent themselves can change their capacity right now.');
        return;
      }
      toast.success(`Capacity → ${n}`);
      loadAll();
    } catch (err) { toast.error('Update failed'); }
  };

  if (loading) return <div className="p-12 text-center text-[#64748B]"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="csm-awa-page">
      <Link to="/admin/csm" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" /> Workspace
      </Link>
      <div className="flex items-center justify-between mt-2 mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2">
            <Settings2 className="w-5 h-5 text-[#0E7490]" /> Advanced Work Assignment
          </h1>
          <p className="text-sm text-[#64748B] mt-1">Auto-route tickets to online agents based on priority, queue, category, and capacity.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={loadAll} variant="outline" size="sm" data-testid="reload-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reload
          </Button>
          <Button onClick={runAwa} size="sm" className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="run-sweep-btn">
            <PlayCircle className="w-3.5 h-3.5 mr-1" /> Run sweep
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E2E8F0] mb-4">
        {['rules', 'agents', 'pool'].map(t => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-[#0E7490] text-[#0E7490]' : 'border-transparent text-[#64748B]'}`}
                  data-testid={`tab-${t}`}>
            {t === 'rules' ? 'Rules' : t === 'agents' ? `Agents (${agents.length})` : `Work pool (${pool.length})`}
          </button>
        ))}
      </div>

      {tab === 'rules' && (
        <div>
          <div className="flex justify-end mb-3">
            <Button size="sm" onClick={() => setShowNew(true)} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-rule-btn">
              <Plus className="w-4 h-4 mr-1" /> New rule
            </Button>
          </div>
          {rules.length === 0 ? (
            <Card className="p-10 text-center border-[#E2E8F0] border-dashed">
              <Settings2 className="w-10 h-10 mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#64748B]">No assignment rules yet. Create one to start auto-routing tickets.</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {rules.map(r => (
                <RuleRow key={r.id} rule={r} agents={agents} groups={groups} queues={queues}
                         onEdit={() => setEditing(r)} onDelete={() => deleteRule(r.id)} />
              ))}
            </div>
          )}
        </div>
      )}

      {tab === 'agents' && (
        <div className="space-y-2">
          {agents.length === 0 ? (
            <Card className="p-10 text-center border-[#E2E8F0] border-dashed">
              <p className="text-sm text-[#64748B]">No agents yet. Users get an agent profile the first time they hit the CSM workspace.</p>
            </Card>
          ) : agents.map(a => (
            <Card key={a.id} className="p-3 border-[#E2E8F0] flex items-center justify-between" data-testid={`agent-card-${a.user_id}`}>
              <div className="flex items-center gap-3">
                <Circle className={`w-3 h-3 fill-current ${a.status === 'online' ? 'text-emerald-500' : a.status === 'away' ? 'text-amber-500' : 'text-slate-400'}`} />
                <div>
                  <div className="text-sm font-medium text-[#0A2342]">{a.name}</div>
                  <div className="text-[11px] text-[#64748B]">{a.email} · {a.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-xs text-right">
                  <div className="text-[#0A2342] font-mono">{a.current_workload}/{a.max_concurrent_tickets}</div>
                  <div className="text-[10px] text-[#94A3B8]">{a.status}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => updateCapacity(a)}>Capacity</Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {tab === 'pool' && (
        <div className="space-y-2">
          {pool.length === 0 ? (
            <Card className="p-10 text-center border-[#E2E8F0] border-dashed">
              <Inbox className="w-10 h-10 mx-auto text-[#94A3B8] mb-2" />
              <p className="text-sm text-[#64748B]">Work pool is empty — every matched ticket has been auto-assigned.</p>
            </Card>
          ) : pool.map(t => (
            <Card key={t.id} className="p-3 border-[#E2E8F0] flex items-center justify-between" data-testid={`pool-row-${t.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-mono text-[#0A2342]">{t.ticket_number}</span>
                  <Badge className={`text-[10px] ${(t.priority || 0) <= 2 ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'} border-0`}>
                    P{t.priority} {PRIORITY_LABELS[t.priority] || ''}
                  </Badge>
                  <span className="text-[#64748B] truncate">{t.subject}</span>
                </div>
                <div className="text-[10px] text-[#94A3B8] mt-0.5">Pooled at {t.awa_pool_at?.slice(0, 16)}</div>
              </div>
              <Button size="sm" onClick={() => claim(t.id)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`claim-${t.id}`}>
                <Hand className="w-3.5 h-3.5 mr-1" /> Claim
              </Button>
            </Card>
          ))}
        </div>
      )}

      {(showNew || editing) && (
        <RuleEditorDialog
          rule={editing}
          agents={agents} groups={groups} queues={queues}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); loadAll(); }}
        />
      )}
    </div>
  );
}

function RuleRow({ rule, agents, groups, queues, onEdit, onDelete }) {
  const c = rule.conditions || {};
  const a = rule.action || {};
  const groupName = a.assignment_group_id ? (groups.find(g => g.id === a.assignment_group_id)?.name || a.assignment_group_id) : null;
  const agentName = a.agent_id ? (agents.find(x => x.user_id === a.agent_id)?.name || a.agent_id) : null;
  return (
    <Card className="p-4 border-[#E2E8F0]" data-testid={`rule-${rule.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge className="bg-slate-100 text-slate-700 border-slate-200 text-[10px]">P{rule.priority}</Badge>
            <h3 className="font-semibold text-[#0A2342] text-sm">{rule.name}</h3>
            {!rule.active && <Badge className="bg-slate-200 text-slate-600 border-0 text-[10px]">Disabled</Badge>}
          </div>
          <div className="text-[11px] text-[#64748B] mt-1 flex flex-wrap gap-2">
            {c.priority_in?.length > 0 && <span>Priority: {c.priority_in.map(p => `P${p}`).join(', ')}</span>}
            {c.category_in?.length > 0 && <span>Category: {c.category_in.join(', ')}</span>}
            {c.queue_ids?.length > 0 && <span>{c.queue_ids.length} queue(s)</span>}
            {c.subject_regex && <span>Regex: <code className="text-[10px] bg-slate-100 px-1 rounded">{c.subject_regex}</code></span>}
            {(!c.priority_in?.length && !c.category_in?.length && !c.queue_ids?.length && !c.subject_regex) && <span className="italic">Matches all tickets</span>}
          </div>
          <div className="text-[11px] mt-1 text-[#0E7490]">
            → {agentName ? `Assigns to ${agentName}` : groupName ? `Group: ${groupName}` : 'All online agents'}
            {a.push_to_online && ' (capacity-aware)'}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={onEdit} data-testid={`edit-rule-${rule.id}`}>
            <Edit className="w-3.5 h-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} className="text-rose-600" data-testid={`del-rule-${rule.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </Card>
  );
}

function RuleEditorDialog({ rule, agents, groups, queues, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    name: rule?.name || '',
    priority: rule?.priority ?? 50,
    active: rule?.active ?? true,
    conditions: {
      queue_ids: rule?.conditions?.queue_ids || [],
      priority_in: rule?.conditions?.priority_in || [],
      category_in: (rule?.conditions?.category_in || []).join(', '),
      customer_org_id: rule?.conditions?.customer_org_id || '',
      subject_regex: rule?.conditions?.subject_regex || '',
    },
    action: {
      assignment_group_id: rule?.action?.assignment_group_id || '',
      agent_id: rule?.action?.agent_id || '',
      push_to_online: rule?.action?.push_to_online !== false,
    },
  }));
  const [saving, setSaving] = useState(false);

  const togglePriority = (p) => {
    setForm(f => {
      const set = new Set(f.conditions.priority_in);
      set.has(p) ? set.delete(p) : set.add(p);
      return { ...f, conditions: { ...f.conditions, priority_in: Array.from(set).sort() } };
    });
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        priority: form.priority,
        active: form.active,
        conditions: {
          queue_ids: form.conditions.queue_ids,
          priority_in: form.conditions.priority_in,
          category_in: form.conditions.category_in.split(',').map(s => s.trim()).filter(Boolean),
          customer_org_id: form.conditions.customer_org_id || null,
          subject_regex: form.conditions.subject_regex || null,
        },
        action: {
          assignment_group_id: form.action.assignment_group_id || null,
          agent_id: form.action.agent_id || null,
          push_to_online: form.action.push_to_online,
        },
      };
      if (rule) {
        await axios.patch(`${API_URL}/api/csm/assignment-rules/${rule.id}`, payload, { headers: authHeader() });
        toast.success('Rule updated');
      } else {
        await axios.post(`${API_URL}/api/csm/assignment-rules`, payload, { headers: authHeader() });
        toast.success('Rule created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="rule-editor-dialog">
        <DialogHeader>
          <DialogTitle>{rule ? 'Edit rule' : 'New assignment rule'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="rule-name-input" />
            </div>
            <div>
              <Label>Priority</Label>
              <Input type="number" min="0" max="1000" value={form.priority}
                     onChange={e => setForm({ ...form, priority: parseInt(e.target.value || '0', 10) })} />
            </div>
          </div>

          <div className="border-t pt-3 border-[#E2E8F0]">
            <div className="text-xs font-semibold text-[#0E7490] mb-2">Conditions</div>

            <Label className="text-xs">Ticket priority</Label>
            <div className="flex gap-1 mt-1 mb-2">
              {[1,2,3,4].map(p => (
                <button key={p} type="button" onClick={() => togglePriority(p)}
                        className={`px-2 py-1 text-[10px] rounded border ${form.conditions.priority_in.includes(p) ?
                          'bg-[#0E7490] border-[#0E7490] text-white' :
                          'bg-white border-[#E2E8F0] text-[#64748B]'}`}>
                  P{p} {PRIORITY_LABELS[p]}
                </button>
              ))}
            </div>

            <div className="mb-2">
              <Label className="text-xs">Categories (comma-separated)</Label>
              <Input placeholder="billing, technical"
                     value={form.conditions.category_in}
                     onChange={e => setForm({ ...form, conditions: { ...form.conditions, category_in: e.target.value } })} />
            </div>

            <div className="mb-2">
              <Label className="text-xs">Queue(s)</Label>
              <select multiple value={form.conditions.queue_ids}
                      onChange={e => setForm({ ...form, conditions: { ...form.conditions, queue_ids: Array.from(e.target.selectedOptions, o => o.value) } })}
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm h-20">
                {queues.map(q => <option key={q.id} value={q.id}>{q.name}</option>)}
              </select>
            </div>

            <div className="mb-2">
              <Label className="text-xs">Subject regex (optional)</Label>
              <Input placeholder="(?i)urgent|down|outage"
                     value={form.conditions.subject_regex}
                     onChange={e => setForm({ ...form, conditions: { ...form.conditions, subject_regex: e.target.value } })} />
            </div>
          </div>

          <div className="border-t pt-3 border-[#E2E8F0]">
            <div className="text-xs font-semibold text-[#0E7490] mb-2">Action</div>
            <Label className="text-xs">Route to specific agent</Label>
            <select value={form.action.agent_id}
                    onChange={e => setForm({ ...form, action: { ...form.action, agent_id: e.target.value, assignment_group_id: '' } })}
                    className="w-full mt-1 mb-2 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm">
              <option value="">— None —</option>
              {agents.map(a => <option key={a.user_id} value={a.user_id}>{a.name}</option>)}
            </select>

            <Label className="text-xs">…or assignment group</Label>
            <select value={form.action.assignment_group_id}
                    onChange={e => setForm({ ...form, action: { ...form.action, assignment_group_id: e.target.value, agent_id: '' } })}
                    className="w-full mt-1 mb-2 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white text-sm">
              <option value="">— None (any online agent) —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>

            <label className="text-xs flex items-center gap-2 mt-2">
              <input type="checkbox" checked={form.action.push_to_online}
                     onChange={e => setForm({ ...form, action: { ...form.action, push_to_online: e.target.checked } })} />
              Push to least-loaded online agent (capacity-aware)
            </label>
          </div>

          <div className="border-t pt-3 border-[#E2E8F0] flex items-center justify-between">
            <label className="text-xs flex items-center gap-2">
              <input type="checkbox" checked={form.active}
                     onChange={e => setForm({ ...form, active: e.target.checked })} />
              Rule active
            </label>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
              <Button onClick={submit} disabled={saving} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="rule-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save rule'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
